import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { useAgencyTalentPermission } from 'shared/hooks/useAgencyTalentPermission';
import { useAxios } from 'shared/hooks/useAxios';
import { IMediaResponse } from 'shared/interfaces/IMedia';
import { IProfileGraphQL } from 'shared/interfaces/IProfile';
import { authToken } from 'shared/utils/authToken';
import Audios from './Audios';
import Documents from './Documents/Documents';
import { MainCont } from './image-video-audio.styles';
import Images from './Images';
import Videos from './Videos';

interface Props {
  permissions: string[];
  nonRepre: boolean;
  profiles: IProfileGraphQL[];
}
const ImagesVideoAudio: React.FC<Props> = ({ permissions, nonRepre, profiles }) => {
  const [fetching, setFetching] = useState(true);
  const queryClient = useQueryClient();

  const { getAuthToken } = authToken();
  const { GET } = useAxios();
  const { profileId } = useParams() as { profileId: string };

  const { isNotifOpen, NotificationModal, handleAgencyTalentPermissions } = useAgencyTalentPermission(
    permissions,
    nonRepre,
  );

  const initializeMedia = useCallback(async () => {
    await GET<IMediaResponse>({
      url: ENDPOINTS.MEDIA,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    setFetching(false);
  }, [GET, getAuthToken]);

  useEffect(() => {
    initializeMedia();
    queryClient.invalidateQueries([
      'profile_media',
      profileId,
      {
        file_type: 'image',
      },
    ]);
    queryClient.invalidateQueries([
      'profile_media',
      profileId,
      {
        file_type: 'audio',
      },
    ]);
    queryClient.invalidateQueries([
      'profile_media',
      profileId,
      {
        file_type: 'video',
      },
    ]);
    queryClient.invalidateQueries([
      'profile_media',
      profileId,
      {
        file_type: 'document',
      },
    ]);
    queryClient.invalidateQueries([
      'media',
      {
        file_type: 'image',
        media_bin: true,
      },
    ]);
    queryClient.invalidateQueries([
      'media',
      {
        file_type: 'audio',
        media_bin: true,
      },
    ]);
    queryClient.invalidateQueries([
      'media',
      {
        file_type: 'video',
        media_bin: true,
      },
    ]);
    queryClient.invalidateQueries([
      'media',
      {
        file_type: 'document',
        media_bin: true,
      },
    ]);
  }, []);

  return (
    <MainCont>
      <Images
        profiles={profiles}
        nonRepre={nonRepre}
        fetching={fetching}
        handleAgencyTalentPermissions={handleAgencyTalentPermissions}
        permissions={permissions}
      />
      <Videos
        nonRepre={nonRepre}
        fetching={fetching}
        handleAgencyTalentPermissions={handleAgencyTalentPermissions}
        permissions={permissions}
      />
      <Audios
        nonRepre={nonRepre}
        fetching={fetching}
        handleAgencyTalentPermissions={handleAgencyTalentPermissions}
        permissions={permissions}
      />
      <Documents
        nonRepre={nonRepre}
        fetching={fetching}
        handleAgencyTalentPermissions={handleAgencyTalentPermissions}
        permissions={permissions}
      />
      {isNotifOpen && <NotificationModal />}
    </MainCont>
  );
};

export default ImagesVideoAudio;
